import type { BufferGeometry } from 'three'
import { arco, curva, type Ponto2 } from '../componentes/contornos2d'
import { aneisDePega, extrusao, torno } from '../componentes/malhasProcedurais'
import type { NivelQualidade } from '../ambiente/qualidadeGrafica'

const DETALHE: Record<NivelQualidade, { passos: number; chanfro: number; torno: number; recartilhado: number }> = {
  alta: { passos: 12, chanfro: 2, torno: 24, recartilhado: 18 },
  media: { passos: 8, chanfro: 2, torno: 18, recartilhado: 12 },
  baixa: { passos: 5, chanfro: 1, torno: 12, recartilhado: 0 },
}

/* --------------------------------------------------------------------------
   Barra Halligan — eixo em x, cunha em +x (contato em x ≈ 0,72), garfo em -x
   -------------------------------------------------------------------------- */

/**
 * Cabo com colares cônicos nas pontas e duas empunhaduras recartilhadas. O
 * recartilhado é uma sequência de cristas finas no perfil do torno: de perto
 * lê como textura, e não custa nada além de vértices.
 */
function perfilCaboHalligan(recartilhado: number): Ponto2[] {
  const empunhadura = (x0: number, x1: number): Ponto2[] => [
    [x0 - 0.005, 0.014],
    [x0 - 0.004, 0.0175],
    [x0 + 0.004, 0.0175],
    [x0 + 0.005, 0.016],
    ...(recartilhado > 0 ? aneisDePega(x0 + 0.006, x1 - 0.006, 0.016, 0.0009, recartilhado) : []),
    [x1 - 0.005, 0.016],
    [x1 - 0.004, 0.0175],
    [x1 + 0.004, 0.0175],
    [x1 + 0.005, 0.014],
  ]
  return [
    [-0.54, 0],
    [-0.54, 0.019],
    [-0.5, 0.0145],
    [-0.49, 0.014],
    ...empunhadura(-0.33, -0.11),
    ...empunhadura(0.11, 0.33),
    [0.49, 0.014],
    [0.5, 0.0145],
    [0.54, 0.02],
    [0.54, 0],
  ]
}

/**
 * Cabeça forjada e cunha num contorno só, visto de lado: o bloco da cabeça
 * afina numa lâmina chata levemente curvada para cima, com o fio em x ≈ 0,725.
 */
function contornoCabecaHalligan(passos: number): Ponto2[] {
  const fundoBloco: Ponto2 = [0.585, -0.022]
  const fioBaixo: Ponto2 = [0.722, 0.03]
  const fioAlto: Ponto2 = [0.727, 0.036]
  const topoBloco: Ponto2 = [0.585, 0.026]
  return [
    [0.528, -0.021],
    fundoBloco,
    ...curva(fundoBloco, [0.64, -0.002], fioBaixo, passos),
    fioAlto,
    ...curva(fioAlto, [0.64, 0.038], topoBloco, passos),
    [0.528, 0.022],
  ]
}

/** Espigão: cone afunilando de forma não linear, que termina numa ponta fina. */
function perfilEspigao(): Ponto2[] {
  const comprimento = 0.145
  const pontos: Ponto2[] = [[0, 0]]
  for (let i = 0; i <= 8; i++) {
    const t = i / 8
    pontos.push([t * comprimento, 0.0175 * Math.pow(1 - t, 1.35) + (i === 8 ? 0 : 0.0008)])
  }
  pontos.push([comprimento, 0])
  return pontos
}

/** Dente do garfo, visto de lado: curva suave para cima, afinando até a ponta. */
function contornoDenteGarfo(passos: number): Ponto2[] {
  const pontaAlta: Ponto2 = [-0.7, 0.058]
  const pontaBaixa: Ponto2 = [-0.706, 0.052]
  return [
    [-0.53, 0.018],
    ...curva([-0.53, 0.018], [-0.62, 0.028], pontaAlta, passos),
    pontaBaixa,
    ...curva(pontaBaixa, [-0.63, 0.006], [-0.53, -0.016], passos),
  ]
}

export interface MalhasFerramentasManuais {
  readonly caboHalligan: BufferGeometry
  readonly cabecaHalligan: BufferGeometry
  readonly espigao: BufferGeometry
  readonly denteGarfo: BufferGeometry
}

const CACHE = new Map<NivelQualidade, MalhasFerramentasManuais>()

/** Geometrias das ferramentas manuais por nível, montadas uma vez e compartilhadas. */
export function malhasFerramentasManuais(nivel: NivelQualidade): MalhasFerramentasManuais {
  const existente = CACHE.get(nivel)
  if (existente) return existente
  const d = DETALHE[nivel]
  const malhas: MalhasFerramentasManuais = {
    caboHalligan: torno(perfilCaboHalligan(d.recartilhado), d.torno),
    cabecaHalligan: extrusao(contornoCabecaHalligan(d.passos), 0.05, 0.004, d.chanfro),
    espigao: torno(perfilEspigao(), d.torno),
    denteGarfo: extrusao(contornoDenteGarfo(d.passos), 0.016, 0.003, d.chanfro),
  }
  CACHE.set(nivel, malhas)
  return malhas
}
